import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MessageSquare, X, Send, Bot, User, ChevronDown,
  Loader2, Trash2, History, Plus, Sparkles, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AIChatAssistant({ clientId = null, clientName = null }) {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load chat sessions
  const loadSessions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get(token, '/ai/chat/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  }, [token]);

  // Load chat history for a session
  const loadHistory = useCallback(async (sid) => {
    if (!token || !sid) return;
    try {
      const response = await api.get(token, `/ai/chat/history/${sid}`);
      const history = response.data.messages || [];
      
      const formattedMessages = [];
      history.forEach(msg => {
        formattedMessages.push({
          id: msg.id + '-user',
          role: 'user',
          content: msg.userMessage,
          timestamp: msg.timestamp
        });
        formattedMessages.push({
          id: msg.id + '-ai',
          role: 'assistant',
          content: msg.aiResponse,
          timestamp: msg.timestamp
        });
      });
      
      setMessages(formattedMessages);
      setSessionId(sid);
      setShowSessions(false);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen, loadSessions]);

  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      const response = await api.post(token, '/ai/chat', {
        message: message.trim(),
        sessionId: sessionId,
        clientId: clientId
      });

      const aiMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Update session ID if new
      if (response.data.sessionId && !sessionId) {
        setSessionId(response.data.sessionId);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I apologize, but I encountered an issue. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(token, `/ai/chat/sessions/${sid}`);
      setSessions(prev => prev.filter(s => s.sessionId !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
      toast.success('Chat deleted');
    } catch (error) {
      toast.error('Failed to delete chat');
    }
  };

  // Quick suggestions
  const suggestions = clientId ? [
    `What should I know about ${clientName || 'this client'}?`,
    'Are there any compliance concerns?',
    'Summarize recent activity'
  ] : [
    'How do I add a new client?',
    'What is a Family Group?',
    'Explain entity structures'
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 md:bottom-6 right-6 w-14 h-14 rounded-full bg-[#4A3B32] text-white shadow-lg hover:bg-[#3A2E26] transition-all flex items-center justify-center z-50 group"
        data-testid="ai-chat-trigger"
      >
        <Sparkles className="w-6 h-6 group-hover:scale-110 transition-transform" />
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-[10px] font-bold">
          AI
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        "fixed bottom-24 md:bottom-6 right-2 md:right-6 bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden transition-all border border-[#E6E0D9] max-w-[calc(100vw-1rem)]",
        isMinimized ? "w-72 h-14" : "w-96 h-[600px] max-h-[calc(100vh-8rem)]"
      )}
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#4A3B32] to-[#5D524D] text-white cursor-pointer"
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-medium text-sm">QIndex AI</h3>
            {!isMinimized && (
              <p className="text-xs text-white/70">
                {clientName ? `Viewing: ${clientName}` : 'Your AI assistant'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
          >
            {isMinimized ? <ChevronDown className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Actions Bar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E6E0D9] bg-[#F9F7F5]">
            <Button
              variant="ghost"
              size="sm"
              onClick={startNewChat}
              className="text-xs h-7 px-2"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Chat
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSessions(!showSessions)}
              className={cn("text-xs h-7 px-2", showSessions && "bg-[#4A3B32]/10")}
            >
              <History className="w-3 h-3 mr-1" />
              History
            </Button>
          </div>

          {/* Sessions List */}
          {showSessions && (
            <div className="absolute top-24 left-3 right-3 bg-white rounded-lg shadow-lg border border-[#E6E0D9] z-10 max-h-64 overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-4 text-center text-sm text-[#8C8078]">
                  No previous chats
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.sessionId}
                    onClick={() => loadHistory(session.sessionId)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2 hover:bg-[#F9F7F5] cursor-pointer border-b border-[#E6E0D9] last:border-0",
                      sessionId === session.sessionId && "bg-[#4A3B32]/5"
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#2C2420] truncate">{session.lastMessage}</p>
                      <p className="text-xs text-[#8C8078]">
                        {new Date(session.lastTimestamp).toLocaleDateString('en-AU')}
                        {' • '}{session.messageCount} messages
                      </p>
                    </div>
                    <button
                      onClick={(e) => deleteSession(session.sessionId, e)}
                      className="p-1 hover:bg-red-50 rounded text-[#8C8078] hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAFA]">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#4A3B32]/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-[#4A3B32]" />
                </div>
                <h4 className="font-medium text-[#2C2420] mb-2">How can I help?</h4>
                <p className="text-sm text-[#5D524D] mb-4">
                  Ask me anything about your clients, entities, or platform features.
                </p>
                <div className="space-y-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setMessage(suggestion)}
                      className="block w-full text-left px-3 py-2 text-sm bg-white rounded-lg border border-[#E6E0D9] hover:border-[#4A3B32] hover:bg-[#F9F7F5] transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex gap-2",
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-[#4A3B32] flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={cn(
                      "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                      msg.role === 'user'
                        ? "bg-[#4A3B32] text-white rounded-tr-sm"
                        : msg.isError
                          ? "bg-red-50 text-red-700 border border-red-200 rounded-tl-sm"
                          : "bg-white text-[#2C2420] border border-[#E6E0D9] rounded-tl-sm shadow-sm"
                    )}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-full bg-[#C6A88F] flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="flex gap-2 items-start">
                <div className="w-7 h-7 rounded-full bg-[#4A3B32] flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 border border-[#E6E0D9] shadow-sm">
                  <div className="flex items-center gap-2 text-[#5D524D]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#E6E0D9] bg-white">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!message.trim() || isLoading}
                className="bg-[#4A3B32] hover:bg-[#3A2E26] px-3"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-[#8C8078] mt-2 text-center">
              Powered by QIndex AI • GPT-5.2
            </p>
          </div>
        </>
      )}
    </div>
  );
}
